import { Fragment, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { Table, Modal, message, Switch, Dropdown, Button, Space, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { fullPagerConfig } from '@/utils/property';
import { get, put } from '@/core/request';
import AddPeople from './components/AddPeople';
import AddLive from './components/AddLive';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import FormalOrganizationsFilterPanel from './FormalOrganizationsFilterPanel';
import './FormalOrganizations.less';

export default function FormalOrganizations() {
    // 渲染table数据的state
    const [tabledata, setTableData] = useState({});
    const [filterObj, setFilterObj] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [addPeople, setAddPeople] = useState(false);
    const [addLive, setAddLive] = useState(false);
    const [rowData, setRowData] = useState(undefined);

    const navigate = useNavigate();

    // 分页设置
    const [pageSize, setPageSize] = useState(10);
    const [currPage, setCurrPage] = useState(1);

    // 改变一页有多少数据
    const changePageSize = useCallback(pageSize => {
        setPageSize(pageSize);
    }, []);

    // 改变第几页
    const changeCurrPage = useCallback(page => {
        setCurrPage(page);
    }, []);

    //分页数据
    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        pageSize,
        size: 'small',
        total: tabledata.totalCount,
        onShowSizeChange: (_current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
        current: currPage,
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedRowKeys, _selectedRows) => {
            setSelectedRowKeys(selectedRowKeys);
        },
    };

    const { code, name, belongsUnitName, adminName, state } = filterObj;

    const getData = useCallback(() => {
        get(`/healthclass/boss/organizations/formalList`, {
            page: currPage,
            pageSize: pageSize,
            code,
            name,
            belongsUnitName,
            adminName,
            state,
        }).then(res => {
            if (res.status_code === 200) {
                setTableData(res.data);
            }
        });
    }, [currPage, pageSize, code, name, belongsUnitName, adminName, state]);

    const handleFilterFinish = value => {
        setFilterObj(value);
        setCurrPage(1);
    };

    const handleReset = () => {
        setFilterObj({});
        setCurrPage(1);
    };

    // 渲染table数据
    useEffect(() => {
        getData();
    }, [getData]);

    const getFormalInfo = row => {
        return () => {
            navigate(`/healthclass/FormalOrganizationsInfo`, { state: row });
        };
    };

    const onSwitchChange = (key, row) => {
        if (key) {
            put('/healthclass/boss/organizations/updateState', {
                id: row.id,
                state: key ? 1 : 2,
            }).then(res => {
                if (res.status_code === 200) {
                    message.success(res.message);
                    navigate('/healthclass/FormalOrganizations');
                }
                getData();
            });
        } else {
            Modal.confirm({
                title: '提示',
                content: '停用后学苑管理员将不能登录，是否确认关停？',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    put('/healthclass/boss/organizations/updateState', {
                        id: row.id,
                        state: key ? 1 : 2,
                    }).then(res => {
                        if (res.status_code === 200) {
                            message.success(res.message);
                            navigate('/healthclass/FormalOrganizations');
                        }
                        getData();
                    });
                },
            });
        }
    };

    const closeAdd = () => {
        setAddPeople(false);
    };
    const closeLive = () => {
        setAddLive(false);
    };

    const addOperation = row => {
        if (row.versionId === 56) {
            message.warning('当前学苑产品版本为基础版，无直播模块，不支持添加直播时长');
        } else {
            setAddLive(true);
            setRowData(row);
        }
    };

    // fixed-scroll 三件套：表格高度自适应 + 横向滚动守卫 + 纵向阴影
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const handleRefresh = useCallback(() => {
        getData();
    }, [getData]);

    const columns = [
        { title: '学苑ID', dataIndex: 'code' },
        { title: '学苑名称', dataIndex: 'name' },
        { title: '所属主体', dataIndex: 'belongsUnitName' },
        {
            title: '主体类型',
            dataIndex: 'belongsUnitType',
            render: (belongsUnitType, _row) => (
                <Fragment>
                    {belongsUnitType === 2 && <span> 机构</span>}
                    {belongsUnitType === 1 && <span> 个人</span>}
                </Fragment>
            ),
        },
        { title: '联系人', dataIndex: 'adminName' },
        { title: '联系人手机', dataIndex: 'phone' },
        { title: '产品版本', dataIndex: 'versionId', render: (versionId, _row) => <Fragment>{versionId === 56 ? <span>基础版</span> : <span>专业版</span>}</Fragment> },
        { title: '人数上限', dataIndex: 'maxUserCount', render: (maxUserCount, _row) => <Fragment>{maxUserCount >= 999999 ? <span>不限</span> : <span>{maxUserCount}人</span>}</Fragment> },
        { title: '直播剩余时长', dataIndex: 'liveTime', render: (liveTime, _row) => <Fragment>{<span>{liveTime}小时</span>}</Fragment> },
        { title: '到期时间', dataIndex: 'endAt' },
        { title: '最近登录时间', dataIndex: 'lastLoggedinAt' },
        {
            title: '状态',
            dataIndex: 'state',
            render: (state, row) => (
                <Fragment>
                    <Switch checked={state === 1} checkedChildren="启用" unCheckedChildren="停用" onChange={key => onSwitchChange(key, row)} />
                </Fragment>
            ),
        },
        {
            title: '操作',
            dataIndex: 'state',
            render: (_belongsUnitType, row) => {
                const items = [
                    {
                        label: '增加成员人数',
                        key: 'people',
                        onClick: () => {
                            setAddPeople(true);
                            setRowData(row);
                        },
                    },
                    {
                        label: '增加直播时长',
                        key: 'live',
                        onClick: () => addOperation(row),
                    },
                ];

                return (
                    <Fragment>
                        <span className="operate-style" onClick={getFormalInfo(row)}>
                            管理
                        </span>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                更多
                            </Button>
                        </Dropdown>
                    </Fragment>
                );
            },
        },
    ];

    return (
        <div className="page-container list-page-container healthclass-formal-page">
            <PageHeader title="正式学苑管理" breadcrumb={[{ label: '健学云', path: '/healthclass/FormalOrganizations' }, { label: '正式学苑管理' }]} />

            <FormalOrganizationsFilterPanel filter={filterObj} onFinish={handleFilterFinish} onReset={handleReset} />

            <div ref={tableRegionRef} className="table-region-container healthclass-formal-table-region">
                <div className="main-toolbar healthclass-formal-toolbar">
                    <div className="main-tools">
                        <span className="table-title">正式学苑列表</span>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap healthclass-formal-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always formalOrg-table' : 'formalOrg-table'}
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={tabledata.list}
                        pagination={paginationProps}
                        rowKey={row => row.id}
                        bordered
                        sticky
                        size="medium"
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                    />
                </div>
            </div>
            <AddPeople addPeople={addPeople} closeAdd={closeAdd} rowData={rowData} getData={getData} />
            <AddLive addLive={addLive} closeLive={closeLive} rowData={rowData} getData={getData} />
        </div>
    );
}
